import { v4 } from "uuid";
import { ACTION_FREQUENCY_TYPE, ACTION_TYPE, addIndexedValues, Allocations, CARBON_SCOPE, createAllocations, createIndexedValues, CURRENT_STATE_PROJECT_KEY, Duration, EDGE_TYPE, EMISSIONS_RATE, FILTER_MODEL_KEY, Frequency, FREQUENCY_TYPE, GraphData, IndexedValues, INSIGHTS_FIELD, isModelNode, isOrganisationNode, LinqsetEdge, LinqsetNode, NODE_SUPERTYPE, NODE_TYPE, NodeInsights, NodeProperties, ORGANISATION_MODEL_KEY, roundAllocations, roundIndexedValues, TUTORIAL_MODEL_KEY, WORK_TYPE } from "./linqset-base";
import { createFilterModel, createModelInsights, createTutorialModel, EMISSION_FACTOR_TYPE, findTagForOrganisation, getModelsForFilter, getUnsortedModelList, InformationAsset, InsightsFilter, MODEL_FUNCTION, ModelDetails, ModelInsights, Organisation, Project, resetModelInsightsCosts, Tag, totalModelInsights } from "./organisation";
import { createLinqsetChanges, getNumberOfChanges, LinqsetChanges } from "./linqset-change-model";
import { USER_ROLE } from "./user";

export { ACTION_FREQUENCY_TYPE, addIndexedValues, type Allocations, createIndexedValues, CURRENT_STATE_PROJECT_KEY, type Duration, EDGE_TYPE, type Emissions, EMISSIONS_RATE, FILTER_MODEL_KEY, type Frequency, FREQUENCY_TYPE, type GraphData, type IndexedValues, INSIGHTS_FIELD, INSIGHTS_SECTION, isModelNode, isOrganisationNode, type LinqNode, type LinqsetEdge, type LinqsetNode, NODE_SUPERTYPE, NODE_TYPE, type NodeInsights, type NodeProperties, ORGANISATION_MODEL_KEY, OUTPUT_VALUE_SOURCE, roundAllocations, roundIndexedValues, sumAllocations, SYSTEM_KIND, TUTORIAL_MODEL_KEY, WORK_TYPE, WORK_TYPE_NAMES } from "./linqset-base";

export const NODE_NAME_MIN = 2
export const NODE_NAME_MAX = 60
export const NODE_DESCRIPTION_MAX = 120
export const NODE_URL_MAX = 100
export const NODE_NOTES_MAX = 500

export const NO_ROLE = "no-role"    // index used in node insights timeUsedByRole to represent time system used without a role

export enum MODEL_TYPE {
    MODELS = "Models only",
    MODELS_CONNECTION = "Models, include connection nodes",
    MODELS_CAPTURE_OUTCOMES = "Models, include capture and outcomes",
    PEOPLE_AND_SYSTEMS = "People, systems, roles and infrastructure",
    PEOPLE = "People and roles",
    SYSTEMS = "Infrastructure and systems",
    //COLLAPSED = "Actions view",
    PROCESS = "All nodes",
    SINGLE_NODE = "All nodes, single people and system nodes",
    ACTIONS = "Actions only",
    INFORMATION = "Information and assets only",
    SYSTEM_INTERFACES = "Information between systems",
    ROLE_HANDOVERS = "Information between roles",
    INFORMATION_ASSETS = 'Information assets',
    SUPPLY_CHAIN = "Supply chain only",
    OVERVIEW = "Overview",
    ROLE_CENTRED = "Role centered",
    SYSTEM_CENTRED = "System centered",
    SWIMLANES_ROLE = "Role swimlanes",
    SWIMLANES_SYSTEM = "System swimlanes",
    SWIMLANES_ROLE_ACTIONS = "Role swimlanes (actions only)",
    SWIMLANES_SYSTEM_ACTIONS = "System swimlanes (actions only)",
    SIPOC = "SIPOC (Suppliers, Inputs, Process, Outputs, Customers)"
}

export enum LAYOUTS {
    HIERARCHIC = "Hierarchic",
    HIERARCHIC_LONGEST = "Hierarchic longest path",
    HIERARCHIC_MAIN = "Hierarchic main path",       // lined up along the main path of each part of the model: where most items go
    HIERARCHIC_TAG = "Hierarchic tag path",         // lined up along the nodes with a tag (see Linqset.layoutTag)
    ORGANIC = "Organic",
    CIRCULAR = "Circular"
}

// the layouts the people and systems views can be opened in: the main path is of the work in a model, and a tag path
// needs a tag
export const PEOPLE_SYSTEMS_LAYOUTS = [LAYOUTS.HIERARCHIC, LAYOUTS.HIERARCHIC_LONGEST, LAYOUTS.ORGANIC, LAYOUTS.CIRCULAR]

const SHOW_TAGS_MODE_TYPES = [MODEL_TYPE.MODELS, MODEL_TYPE.MODELS_CONNECTION, MODEL_TYPE.MODELS_CAPTURE_OUTCOMES, MODEL_TYPE.ACTIONS, MODEL_TYPE.SINGLE_NODE, MODEL_TYPE.SUPPLY_CHAIN, MODEL_TYPE.PROCESS]

export const ORGANISATION_VIEW_MODEL_TYPES = [MODEL_TYPE.PEOPLE_AND_SYSTEMS, MODEL_TYPE.MODELS, MODEL_TYPE.MODELS_CONNECTION, MODEL_TYPE.MODELS_CAPTURE_OUTCOMES, MODEL_TYPE.PEOPLE, MODEL_TYPE.SYSTEMS]

// the views of the organisation's people and systems, which open in the layout the user last chose for them
export const PEOPLE_SYSTEMS_VIEWS = [MODEL_TYPE.PEOPLE_AND_SYSTEMS, MODEL_TYPE.PEOPLE, MODEL_TYPE.SYSTEMS]

export const isReadOnlyModelType = (t:MODEL_TYPE) => {
    return t !== MODEL_TYPE.PROCESS && t !== MODEL_TYPE.PEOPLE_AND_SYSTEMS
}

export const showTagsForModelType = (t:MODEL_TYPE) => {
    return SHOW_TAGS_MODE_TYPES.indexOf(t) >= 0
}

export const getActionFrequencyTypeShortName = (t:ACTION_FREQUENCY_TYPE) => {
    switch (t) {
        case ACTION_FREQUENCY_TYPE.MAXIMUM:
            return "max"
        case ACTION_FREQUENCY_TYPE.MINIMUM:
            return "min"
        case ACTION_FREQUENCY_TYPE.AVERAGE:
            return "avg"
        case ACTION_FREQUENCY_TYPE.SUM:
        default:
            return "sum"
    }
}

export const getSuperTypeName = (st:NODE_SUPERTYPE) => {
    switch(st) {
        case NODE_SUPERTYPE.INFORMATION:
            return "Information"
        case NODE_SUPERTYPE.ACTION:
            return "Action"
        case NODE_SUPERTYPE.SYSTEM:
            return "System"
        case NODE_SUPERTYPE.ROLE:
            return "Role"
        case NODE_SUPERTYPE.PEOPLE:
            return "People"
        case NODE_SUPERTYPE.INFRASTRUCTURE:
            return "Infrastructure"
        case NODE_SUPERTYPE.NOTES:
            return "Notes"
        case NODE_SUPERTYPE.OUTCOMES:
            return "Outcome"
    }
}

export const getTypeName = (t:NODE_TYPE) => {
    switch(t) {
        case NODE_TYPE.INFORMATION:
            return "Info"
        case NODE_TYPE.AI_AGENT:
            return "AI"
        case NODE_TYPE.APPLICATION:
            return "App"
        case NODE_TYPE.CAPTURE:
            return "Capture"
        case NODE_TYPE.DATABASE:
            return "DB"
        case NODE_TYPE.EXTERNAL_ORGANISATION:
            return "Ext"
        case NODE_TYPE.FILE:
            return "File"
        case NODE_TYPE.OUTCOME:
            return "Outcome"
        case NODE_TYPE.PAPER:
            return "Paper"
        case NODE_TYPE.PROCESS:
            return "Process"
        case NODE_TYPE.SERVICE:
            return "Service"
        case NODE_TYPE.SOMETHING_HAPPENS_HERE:
            return "Other"
        case NODE_TYPE.TEAM:
            return "Team"
        case NODE_TYPE.TODO:
            return "Todo"
        case NODE_TYPE.VERBAL:
            return "Verbal"
        case NODE_TYPE.ACTION:
            return "Action"
        case NODE_TYPE.SYSTEM:
            return "System"
        case NODE_TYPE.ROLE:
            return "Role"
        case NODE_TYPE.PERSON:
            return "People"
        case NODE_TYPE.INFRASTRUCTURE:
            return "Infrastructure"
        case NODE_TYPE.CLOUD_BASED:
            return "Cloud based"
        case NODE_TYPE.ON_PREMISE:
            return "On premise"
        case NODE_TYPE.COMMENT:
            return "Comment"
        case NODE_TYPE.OUTCOMES:
            return "Outcome"
        case NODE_TYPE.ASSET:
            return "Information asset"
        case NODE_TYPE.MODEL:
            return "Model"
        default:
            return ""
    }
}

export const getTypeDescription = (t:NODE_TYPE) => {
    switch(t) {
        case NODE_TYPE.INFORMATION:
            return "Data, information or entity created and managed by actions."
        case NODE_TYPE.AI_AGENT:
            return "AI process or agent that performs actions."
        case NODE_TYPE.APPLICATION:
            return "Application data or configuration."
        case NODE_TYPE.CAPTURE:
            return "Capture action where information is received into the process."
        case NODE_TYPE.DATABASE:
            return "Data held or supplied by a database."
        case NODE_TYPE.EXTERNAL_ORGANISATION:
            return "External organisation that does work as part of a process."
        case NODE_TYPE.FILE:
            return "File or document used in the process."
        case NODE_TYPE.OUTCOME:
            return "Outcome of a process."
        case NODE_TYPE.PAPER:
            return "Information represented on paper"
        case NODE_TYPE.PROCESS:
            return "Process that does work."
        case NODE_TYPE.SERVICE:
            return "Informatio provided as a service."
        case NODE_TYPE.SOMETHING_HAPPENS_HERE:
            return "Other action"
        case NODE_TYPE.TEAM:
            return "Team of people within the organisation."
        case NODE_TYPE.TODO:
            return "List of things to do when working on a model."
        case NODE_TYPE.VERBAL:
            return "Information represented as a verbal message or conversation."
        case NODE_TYPE.ACTION:
            return "Generic action that performs work."
        case NODE_TYPE.SYSTEM:
            return "System or application that performs actions."
        case NODE_TYPE.ROLE:
            return "Defines who can perform an action."
        case NODE_TYPE.PERSON:
            return "People that perform roles within the organisation."
        case NODE_TYPE.INFRASTRUCTURE:
            return "Infrastructure that support systems within organisation."
        case NODE_TYPE.CLOUD_BASED:
            return "Cloud based infrastructure"
        case NODE_TYPE.ON_PREMISE:
            return "Infrastructure deployed on premise or within own data center"
        case NODE_TYPE.COMMENT:
            return "Any comment or note about the model."
        case NODE_TYPE.OUTCOMES:
            return "Business outcome of a process."
        case NODE_TYPE.ASSET:
            return "Information assets held within the organisation"
        case NODE_TYPE.MODEL:
            return "Process models that make up the organisation"
        default:
            return ""
    }
}

export const getSuperType = (type:NODE_TYPE):NODE_SUPERTYPE => {
    switch (type) {
        case NODE_TYPE.INFORMATION:
        case NODE_TYPE.APPLICATION:
        case NODE_TYPE.SERVICE:
        case NODE_TYPE.DATABASE:
        case NODE_TYPE.FILE:
        case NODE_TYPE.PAPER:
        case NODE_TYPE.VERBAL:
            return NODE_SUPERTYPE.INFORMATION
        case NODE_TYPE.ACTION:
        case NODE_TYPE.CAPTURE:
        case NODE_TYPE.PROCESS:
        case NODE_TYPE.OUTCOME:
        case NODE_TYPE.SOMETHING_HAPPENS_HERE:
            return NODE_SUPERTYPE.ACTION
        case NODE_TYPE.SYSTEM:
        case NODE_TYPE.AI_AGENT:
            return NODE_SUPERTYPE.SYSTEM
        case NODE_TYPE.ROLE:
        case NODE_TYPE.EXTERNAL_ORGANISATION:
            return NODE_SUPERTYPE.ROLE
        case NODE_TYPE.PERSON:
        case NODE_TYPE.TEAM:
            return NODE_SUPERTYPE.PEOPLE
        case NODE_TYPE.INFRASTRUCTURE:
        case NODE_TYPE.ON_PREMISE:
        case NODE_TYPE.CLOUD_BASED:
            return NODE_SUPERTYPE.INFRASTRUCTURE
        case NODE_TYPE.COMMENT:
        case NODE_TYPE.TODO:
            return NODE_SUPERTYPE.NOTES
        case NODE_TYPE.OUTCOMES:
            return NODE_SUPERTYPE.OUTCOMES
        default:
            return NODE_SUPERTYPE.ACTION
    }
}

export const getTypeForSuperType = (superType:NODE_SUPERTYPE) => {
    switch(superType) {
        case NODE_SUPERTYPE.ACTION:
            return NODE_TYPE.ACTION
        case NODE_SUPERTYPE.INFORMATION:
            return NODE_TYPE.INFORMATION
        case NODE_SUPERTYPE.INFRASTRUCTURE:
            return NODE_TYPE.INFRASTRUCTURE
        case NODE_SUPERTYPE.NOTES:
            return NODE_TYPE.COMMENT
        case NODE_SUPERTYPE.OUTCOMES:
            return NODE_TYPE.OUTCOMES
        case NODE_SUPERTYPE.PEOPLE:
            return NODE_TYPE.PERSON
        case NODE_SUPERTYPE.ROLE:
            return NODE_TYPE.ROLE
        case NODE_SUPERTYPE.SYSTEM:
            return NODE_TYPE.SYSTEM
    }
}

// these are no longer used, but kept them to import old files for now
export enum COST_TYPE {
    USAGE_PER_HOUR,
    FIXED_PER_ANNUM,
    FIXED_PER_MONTH,
    AVERAGE_PEOPLE
}

export enum NODE_WARNING {
    MISSING_NAME = "name",
    MISSING_COST = "cost",
    MISSING_OUTPUTVALUE = "value",
    MISSING_DURATION = "duration",
    MISSING_FREQUENCY = "frequency",
    OVERRIDING_FREQUENCY = "overriding-frequency",
    MISSING_CARBON = "carbon",
    MISSING_CAPACITY = "capacity",
    OVER_CAPACITY = "over-capacity",
    NOT_IN_MODEL = "not-in-model",
    MISSING_REVENUE = "revenue",
    MISSING_ROLE_SYSTEM = "missing-role-system",
    JOINED_VALUE = "joined-value",             // a value or revenue set on an output joined into another model, which isn't counted
}

export const getWarningText=(warning:NODE_WARNING):string => {
    switch (warning) {
        case NODE_WARNING.MISSING_NAME:
        case NODE_WARNING.MISSING_COST:
        case NODE_WARNING.MISSING_DURATION:
        case NODE_WARNING.MISSING_FREQUENCY:
        case NODE_WARNING.MISSING_CARBON:
        case NODE_WARNING.MISSING_CAPACITY:
            return "Missing " + warning.toString()
        case NODE_WARNING.MISSING_OUTPUTVALUE:
            return "Missing output value"
        case NODE_WARNING.OVER_CAPACITY:
            return "Over capacity"
        case NODE_WARNING.NOT_IN_MODEL:
            return "Not used in a model"
        case NODE_WARNING.OVERRIDING_FREQUENCY:
            return "Node overrides frequency"
        case NODE_WARNING.MISSING_REVENUE:
            return "Missing revenue"
        case NODE_WARNING.MISSING_ROLE_SYSTEM:
            return "No role or system"
        case NODE_WARNING.JOINED_VALUE:
            return "Value set on a joined output"
        default: return ""
    }
}

export const DAYS_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export const getEdgeDescription = (edgeType:EDGE_TYPE) => {
    switch (edgeType) {
        case EDGE_TYPE.AUTOMATE:
            return "automates"
        case EDGE_TYPE.CREATE:
            return "creates"
        case EDGE_TYPE.PERFORM:
            return "performs"
        case EDGE_TYPE.SUPPLY:
            return "supplies"
        case EDGE_TYPE.SUPPORT:
            return "supports"
        case EDGE_TYPE.USEBY:
            return "used by"
        case EDGE_TYPE.FULFILS:
            return "fulfils"
        case EDGE_TYPE.PROVIDES:
            return "provides"
        case EDGE_TYPE.JOINS:
            return "joins to"
        case EDGE_TYPE.ATTACHED:
            return "attached to"
        case EDGE_TYPE.REALISE:
            return "realise"
        case EDGE_TYPE.USED_IN:
            return "is used in"

        default: return ""

    }
}

export const getEmissionRateDescription = (rate:EMISSIONS_RATE) => {
    switch (rate) {
        case EMISSIONS_RATE.HOUR:
            return `per hour`
        case EMISSIONS_RATE.UNITS_HOUR:
            return "per unit per hour"
        case EMISSIONS_RATE.YEAR:
            return "per year"
        case EMISSIONS_RATE.PEOPLE_YEAR:
            return "per person per year"
        case EMISSIONS_RATE.UNITS_YEAR:
            return "per unit per year"
        case EMISSIONS_RATE.LIFETIME:
            return "per lifespan"
        case EMISSIONS_RATE.PEOPLE_LIFETIME:
            return "per person per lifespan"
        case EMISSIONS_RATE.UNITS_LIFETIME:
            return "per unit per lifespan"
    }
}

export const SYSTEM_EDGE_TYPES = [EDGE_TYPE.SUPPLY, EDGE_TYPE.USEBY, EDGE_TYPE.AUTOMATE, EDGE_TYPE.PROVIDES]
export const ROLE_EDGE_TYPES = [EDGE_TYPE.PERFORM]
export const SUPPLY_CHAIN_EDGE_TYPES = [EDGE_TYPE.CREATE, EDGE_TYPE.SUPPORT, EDGE_TYPE.PROVIDES]

export interface AIGenerationData {
    organisation?:string     // the name of the organisation
    industry:string         // type of industry
    function:string          // process category. comes from model function
    description:string,     // high level description of what the business process is
    captures:string[],         // describe the capture actions and data coming in
    roles:string[],         // list of roles involved
    systems:string[],       // list of systems used
    transcript:string,      // detailed description of the process or text file that has been uploaded.
    outcomes:string[]       // list of outcomes
    fileType?:string        // if a file is being used, the type of file
    processDescription?:string  // a description of the process the user has checked. When given, the model is built from it rather than describing the process again
}

export const AI_PROCESS_DESCRIPTION_MAX = 20000

export enum LINQSET_TYPE {
    MODEL,              // single model
    ORGANISATION,       // organisation model
    MODEL_FILTER,       // filter containing models
    NODE_FILTER,        // filter containing nodes
    CAPTURE,            // trace from a particular capture node dwonstream to its outcomes
    OUTCOME,            // trace from an outcome upstream to its capture nodes
    TUTORIAL            // tutorial
}

export const isFilterType = (type:LINQSET_TYPE) => {
    return type === LINQSET_TYPE.CAPTURE || type === LINQSET_TYPE.OUTCOME || type === LINQSET_TYPE.MODEL_FILTER || type === LINQSET_TYPE.NODE_FILTER
}

// a name people typed, for the html in a description. Shared code can't use the client's escapeHtml, so it is here too
const htmlText = (value:any) => String(value ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')

export class Linqset {
    type:LINQSET_TYPE = LINQSET_TYPE.ORGANISATION
    organisationKey:string
    workspaceKey:string = CURRENT_STATE_PROJECT_KEY
    model:ModelDetails
    modelType:MODEL_TYPE = MODEL_TYPE.PROCESS
    layout:LAYOUTS = LAYOUTS.HIERARCHIC
    layoutTag:{groupKey:string, tagKey:string}|undefined   // the tag a "Hierarchic tag path" layout lines the model up by
    project:Project|undefined
    filterNodes:LinqsetNode[] = []        // if nodes selected as a filter
    insights:ModelInsights
    maxNodeValue:number = 0                 // maximum node value of currently selected label. used for heatmap and weighting
    maxEdgeValue:number = 0                 // maximum edge value of currently selected label. used for heatmap and weighting

    modelKeys: string[] = []           // maybe be filtered by certain models

    nodes:LinqsetNode[] = []
    edges:LinqsetEdge[] = []

    changes: LinqsetChanges
    refresh:string|undefined = undefined     // have we made changes and so need to notify server to refresh insights. Used so dont have to do it every time a change is made
    readonlyOverride:boolean|undefined = undefined      // force linqset to be readonly. used with filtered linqsets

    constructor(public organisation:Organisation, public filter:InsightsFilter, graphData?:GraphData, organisationNodes?:LinqsetNode[]) {
        this.organisationKey = organisation._key
        this.workspaceKey = filter.workspaceKey || CURRENT_STATE_PROJECT_KEY
        this.type = LINQSET_TYPE.MODEL_FILTER      // default
        this.model = createFilterModel(this.workspaceKey)   // default
        this.project = (this.workspaceKey !== CURRENT_STATE_PROJECT_KEY)?this.organisation.projects?.find(p => p._key === this.workspaceKey):undefined

        // start with new insights, gets overridden below if existing
        this.insights = createModelInsights(this.organisation._key, this.workspaceKey, FILTER_MODEL_KEY)

        this.setModelAndInsights()
        this.changes = createLinqsetChanges()

        this.modelType = this.isOrganisationModel()?MODEL_TYPE.MODELS:MODEL_TYPE.PROCESS

        if (graphData) {
            this.setupGraph(graphData, organisationNodes)
        }
        if (this.type === LINQSET_TYPE.MODEL_FILTER) this.insights = totalModelInsights(this.models, this.workspaceKey, this.organisationKey, this.nodes)
        // other filters get their insights from model processing, which builds on this file: create them with createLinqset
    }

    get reloadOnSave() {
        if (this.changes.newModel) return true
        if (this.project && this.model.projects && this.model.projects.indexOf(this.project._key) < 0) return true
        return false
    }

    get readonly() {
        if (this.readonlyOverride !== undefined) return this.readonlyOverride
        if (isReadOnlyModelType(this.modelType)) return true
        if (this.isOrganisationModel()) return this.organisation.role === USER_ROLE.READ
        return this.model.role === USER_ROLE.READ
    }

    get workspaceName() {
        if (this.isCurrentState()) {
            return "Current state"
        }else {
            if (this.project) return this.project.name
        }
        return ""
    }

    get name() {
        return this.model.name
    }

    get description() {
        let m = ''

        if (this.type === LINQSET_TYPE.MODEL) m = `Single model named <b>${htmlText(this.name)}</b>. `
        if (this.type === LINQSET_TYPE.ORGANISATION) m = "Organisation view of all models, people and systems. "
        if (this.type === LINQSET_TYPE.TUTORIAL) return "Tutorial for learning how to build models. "

        if (this.type === LINQSET_TYPE.MODEL_FILTER) {
            m = "Filter based on models: <b>" + this.models.map(m => htmlText(m.name)).join(", ") + "</b>. "
        }

        if (this.type === LINQSET_TYPE.CAPTURE) m = "Trace downstream from capture node <b>" + htmlText(this.filterNodes[0]?.name) + "</b>. "
        else if (this.type === LINQSET_TYPE.OUTCOME) m = "Trace upstream from outcome <b>" + htmlText(this.filterNodes[0]?.name) + "</b>. "
        else {
            if (this.filterNodes.length > 0) m += `Filter based on nodes: <b>${this.filterNodes.map(n => htmlText(n.name)).join(", ")}</b>. `
            if (this.filter.informationAssetKeys.length > 0) m += `Filter based on information assets: <b>${this.informationAssets.map(n => htmlText(n.name)).join(", ")}</b>. `
        }

        if (this.filter.nodeTags.length > 0) {
            if (m.length > 0)
                m += ' and node tags: '
            else m = 'Filter based on node tags: '

            m += `<b>${this.nodeTags.map(n => htmlText(n.name)).join(", ")}</b>`
        }

        if (this.filter.modelKeys.length > 1)
            return m + " and the models: <b>" + this.models.map(m => htmlText(m.name)).join(", ") + "</b>. "

        return m

    }

    get informationAssets():InformationAsset[] {
        if (this.filter.informationAssetKeys.length > 0) {
            return this.filter.informationAssetKeys.map(a => this.organisation.informationAssets?.find(ia => ia._key === a)).filter(ia => ia !== undefined) as InformationAsset[]
        }
        return []
    }

    get modelFunctions():MODEL_FUNCTION[] {
        return this.filter.modelFunctions
    }

    get modelTags():Tag[] {
        if (!this.organisation.modelTags) return []
        return this.filter.modelTags.map((tk:string) => this.organisation.modelTags.tags.find((t:Tag) => t._key === tk)).filter(t => t !== undefined)
    }

    get nodeTags():Tag[] {
        const tags:Tag[] = []
        this.filter.nodeTags.forEach((tk:string) => {
            for (const tg of this.organisation.tagGroups || []) {
                const tag = tg.tags.find(t => t._key === tk)
                if (tag) {
                    tags.push(tag)
                    break
                }
            }
        })
        return tags
    }

    get modelKey() {
        return this.model._key
    }

    get hasModelNodes() {
        return (this.type === LINQSET_TYPE.MODEL_FILTER ||  this.type === LINQSET_TYPE.ORGANISATION)?false:true
    }

    get defaultModelType() {
        return this.hasModelNodes?MODEL_TYPE.PROCESS:MODEL_TYPE.MODELS
    }

    get models() {
        if (this.modelKeys.length>0) {
            return this.organisation.models.filter(m => this.modelKeys.indexOf(m._key) >= 0)
        }else {
            return getUnsortedModelList(this.organisation, this.project)
        }
    }

    // list of models that have been changed in the selected project. Empty if doing current state
    get projectModelKeys() {
        let projectModelKeys:string[] = []
        if (this.project) {
            if (this.project.sandbox) projectModelKeys = this.modelKeys
            else {
                projectModelKeys = this.models.filter(m => m.projects.indexOf(this.project!._key) >= 0).map(m => m._key)
            }
        }
        return projectModelKeys
    }

    private setupGraph(graph:GraphData, organisationNodes?:LinqsetNode[]) {
        if (this.type === LINQSET_TYPE.ORGANISATION || this.type === LINQSET_TYPE.MODEL_FILTER) {
            this.populateOrganisationLinqset(graph)
        }else {
            if (organisationNodes)
                this.populateModelLinqset(graph, organisationNodes)
        }
    }

    getEdgesFromNode(node:LinqsetNode) {
        return this.edges.filter(e => e.sourceKey === node._key)
    }

    getEdgesToNode(node:LinqsetNode) {
        return this.edges.filter(e => e.targetKey === node._key)
    }

    isDirty() {
        return getNumberOfChanges(this.changes) > 0
    }

    resetChanges() {
        this.changes = createLinqsetChanges()
    }

    getNode(key:string) {
        return this.nodes.find(n => n._key === key)
    }

    isProcessModel() {
        return !this.isOrganisationModel() && !this.isFilterModel()
    }

    isFilterModel() {
        return isFilterType(this.type)
    }

    isOrganisationModel() {
        return this.type === LINQSET_TYPE.ORGANISATION
    }

    isCurrentState() {
        return this.workspaceKey === CURRENT_STATE_PROJECT_KEY
    }

    changedByProject() {
        return this.isProjectModel() && this.model?.projects && this.model.projects.indexOf(this.workspaceKey)>=0
    }

    isProjectModel() {
        return this.workspaceKey !== CURRENT_STATE_PROJECT_KEY
    }

    isTutorial() {
        return this.modelKey === TUTORIAL_MODEL_KEY
    }

    addRemovedNodesToLinqset(comparison:Linqset) {
        const edges:LinqsetEdge[] = []
        const nodes:LinqsetNode[] = []

        if (comparison) {
            comparison.nodes.filter(n => !this.getNode(n._key)).forEach(n => {
                comparison.edges.filter(e => e.sourceKey === n._key || e.targetKey === n._key).forEach(e => {
                    // check first so dont double up
                    if (!edges.find(ee => ee._key === e._key)) {
                        edges.push({...e, projectKey:this.workspaceKey, excluded:true})
                    }
                })

                nodes.push({...n, projectKey:this.workspaceKey, excluded:true})
            })

            if (nodes.length > 0) {
                this.nodes = this.nodes.concat(nodes)
                this.edges = this.edges.concat(edges)
            }
        }
    }

    removeRemovedNodesToLinqset() {
        this.nodes = this.nodes.filter(n => !n.excluded)
        this.edges = this.edges.filter(e => !e.excluded)
    }

    getAssociatedRolesAndSystems(n:LinqsetNode):{roles:LinqsetNode[], systems:LinqsetNode[]} {
        const roles:LinqsetNode[] = []
        const systems:LinqsetNode[] = []

        const edges = this.edges.filter(e => e.targetKey === n._key && (e.type === EDGE_TYPE.PERFORM || e.type === EDGE_TYPE.AUTOMATE || e.type === EDGE_TYPE.SUPPLY))
        edges.forEach(e => {
            const node = this.getNode(e.sourceKey)
            if (node) {
                if (node.superType === NODE_SUPERTYPE.ROLE) {
                    roles.push(node)
                    const systemEdge = this.edges.find(e2 => e2.targetVisualisationKey === e.sourceVisualisationKey && e.type === EDGE_TYPE.USEBY)
                    if (systemEdge) {
                        const systemNode = this.getNode(systemEdge.sourceKey)
                        if (systemNode) systems.push(systemNode)
                    }
                }else systems.push(node)
            }
        })
        return {roles, systems}
    }

    private setModelAndInsights() {
        if (this.filter.informationAssetKeys.length > 0 || this.filter.nodeKeys.length > 0 || this.filter.nodeTags.length > 0) {
            this.type = LINQSET_TYPE.NODE_FILTER
        }

        const filteredModels = getModelsForFilter(this.filter, this.organisation, this.project)
        this.modelKeys = filteredModels.map(m => m._key)

        if (this.filter.modelKeys.length === 0 && this.filter.modelFunctions.length === 0 && this.filter.modelTags.length === 0) {
            // if no models selected and no nodes selected it is the organisation model and insights
            if (this.type === LINQSET_TYPE.MODEL_FILTER) {
                // organisation model
                this.type = LINQSET_TYPE.ORGANISATION
                this.model = this.organisation.model
                this.insights = this.model?.insights?.find(i => i.projectKey === this.workspaceKey) || createModelInsights(this.organisation._key, this.workspaceKey, ORGANISATION_MODEL_KEY)
            }
        }else if (this.filter.modelKeys.length === 1) {
            //single model selected
            if (this.filter.modelKeys[0] === TUTORIAL_MODEL_KEY) {
                this.type = LINQSET_TYPE.TUTORIAL
                this.model = createTutorialModel()
                this.insights = createModelInsights(this.organisation._key, this.workspaceKey, TUTORIAL_MODEL_KEY)
            }else if (this.type !== LINQSET_TYPE.NODE_FILTER) {
                // single model selected
                this.type = LINQSET_TYPE.MODEL
                const find = this.organisation.models.find(m => m._key === this.modelKeys[0])
                if (!find) throw Error("Invalid model key in Linqset setup")
                this.model = find
                this.insights = this.model?.insights?.find(i => i.projectKey === this.workspaceKey) || createModelInsights(this.organisation._key, this.workspaceKey, this.modelKeys[0])
            }
        }
    }

    private populateOrganisationLinqset(graphData:GraphData) {
        // use edgemap to ensure we only have one instance of an edge between two particular nodes. mainly for people and systems view
        const edgeMap:any = {}
        const modelKeys = this.projectModelKeys
        const actionNodes:any = {}

        this.nodes = graphData.nodes.filter(n => {
            // will add relevent actions when processing edges
            if (n.superType === NODE_SUPERTYPE.ACTION && n.type !== NODE_TYPE.CAPTURE && n.type !== NODE_TYPE.OUTCOME) {
                actionNodes[n._key] = n
                return false
            }

            return (n.projectKey === this.workspaceKey || (!this.project?.sandbox && n.projectKey === CURRENT_STATE_PROJECT_KEY))
        })

        // filter edges by workspace. includes system to role relationships and joins that come from models
        this.edges = graphData.edges.filter(e => {
            if (e.projectKey === this.workspaceKey || (!this.project?.sandbox && e.projectKey === CURRENT_STATE_PROJECT_KEY && modelKeys.indexOf(e.modelKey) < 0)) {
                // check that issnt a repeat
                if (!edgeMap[e.sourceKey]) edgeMap[e.sourceKey] = []
                else if (edgeMap[e.sourceKey].indexOf(e.targetKey) >= 0) return false

                edgeMap[e.sourceKey].push(e.targetKey)

                if ((e.type === EDGE_TYPE.JOINS || e.type === EDGE_TYPE.USED_IN) && actionNodes[e.targetKey]) {
                   // add in action nodes that have edges.
                    this.nodes.push(actionNodes[e.targetKey])
                    actionNodes[e.targetKey] = undefined
                }
                return true
            }
            return false
        })
    }

    private findNode(key:string, nodes:LinqsetNode[], organisationNodes:LinqsetNode[]) {
        let n = this.getNode(key)
        if (!n) {
            const organisationNode = organisationNodes.find(n => n._key === key)
            const graphNode = nodes.find(n => n._key === key)
            n = organisationNode || graphNode
            if (organisationNode && graphNode && organisationNode !== graphNode) this.mergeModelInsights(graphNode, organisationNode)
            if (n) this.nodes.push(n)
        }
        return n
    }

    // A node shared between models (e.g. an external output) is taken from the organisation's nodes, whose insights
    // were loaded for the organisation's workspace only. The copy that came with this model also has the model's
    // insights for other workspaces (e.g. current state when viewing a project), which comparisons need, so keep them.
    private mergeModelInsights(from:LinqsetNode, to:LinqsetNode) {
        if (!from.insights || from.insights.length === 0) return
        if (!to.insights) to.insights = []
        from.insights.forEach(i => {
            const index = to.insights.findIndex(t => t.modelKey === i.modelKey && t.projectKey === i.projectKey)
            if (index < 0) to.insights.push(i)
            else if (this.modelKeys.indexOf(i.modelKey) >= 0) to.insights[index] = i     // loaded with this model, so current
        })
    }

    private populateModelLinqset(graph:GraphData, organisationNodes:LinqsetNode[]=[]) {
        // list of models that have been changed in the selected project. Empty if doing current state
        const projectModelKeys = this.projectModelKeys

        this.edges = []
        this.nodes = []

        graph.edges.filter(e => {
            if ((this.type === LINQSET_TYPE.MODEL || this.type === LINQSET_TYPE.MODEL_FILTER) && this.modelKeys.indexOf(e.modelKey) < 0) return false
            if (this.isCurrentState() || projectModelKeys.indexOf(e.modelKey) < 0) return e.projectKey === CURRENT_STATE_PROJECT_KEY
            else return e.projectKey === this.workspaceKey
        }).forEach(e => {
            if (!this.edges.find(e2 => e2._key === e._key)) {
                const sourceNode = this.findNode(e.sourceKey, graph.nodes, organisationNodes)
                const targetNode = this.findNode(e.targetKey, graph.nodes, organisationNodes)
                if (targetNode && sourceNode) {
                    this.edges.push(e)
                }else {
                    this.changes.deletedEdges.push(e)
                }
            }
        })

        // the model's own nodes that nothing is joined to (a step added and saved before it was named or connected):
        // kept, so they show on its canvas to be named, connected or deleted rather than staying unseen in the data
        if (this.type === LINQSET_TYPE.MODEL) {
            const added = new Set(this.nodes.map(n => n._key))
            graph.nodes.filter(n => {
                if (this.modelKeys.indexOf(n.modelKey) < 0 || added.has(n._key)) return false
                if (this.isCurrentState() || projectModelKeys.indexOf(n.modelKey) < 0) return n.projectKey === CURRENT_STATE_PROJECT_KEY
                else return n.projectKey === this.workspaceKey
            }).forEach(n => this.nodes.push(n))
        }

        if (this.filter.informationAssetKeys.length > 0 || this.filter.nodeKeys.length > 0 || this.filter.nodeTags.length > 0) {
            if (this.filter.nodeKeys.length > 0) {
                this.filterNodes = this.nodes.filter(n => this.filter.nodeKeys.indexOf(n._key) >= 0)
                if (this.filterNodes.length === 1) {
                    if (this.filterNodes[0].type === NODE_TYPE.CAPTURE) this.type = LINQSET_TYPE.CAPTURE
                    if (this.filterNodes[0].superType === NODE_SUPERTYPE.OUTCOMES) this.type = LINQSET_TYPE.OUTCOME
                }
            }
        }

    }
}

export const durationEquals = (duration1:Duration|undefined, duration2:Duration|undefined) => {
    if (!duration1 && !duration2) return true
    if (!duration1 || !duration2) return false

    return ((duration1.days||0) === (duration2.days||0) && (duration1.minutes||0) === (duration2.minutes||0) && (duration1.hours||0) === (duration2.hours||0) && (duration1.seconds||0) === (duration2.seconds||0))
}

export const getDurationHours=(duration?:Duration) => {
    if (!duration) return 0

    const totalSeconds = ((duration.days?duration.days * 3600*24:0) +
        (duration.hours?duration.hours * 3600:0) +
        (duration.minutes?duration.minutes * 60:0) +
        (duration.seconds?duration.seconds:0))

    return totalSeconds/3600
}

export const getDurationDescription=(duration?:Duration) => {
    if (!duration) return '0 hours'

    const result = ((duration.days?duration.days.toString() + (duration.days===1?" day ":" days "):"")) +
                    (duration.hours?duration.hours.toString() + (duration.hours===1?" hour ":" hours "):"") +
                    (duration.minutes?duration.minutes.toString() + (duration.minutes===1?" minute ":" minutes "):"") +
                    (duration.seconds?duration.seconds.toString() + (duration.seconds===1?" second ":" seconds "):"")
    return (result.length > 0)?result:"0 hours"
}

export const frequencyEquals = (frequency1:Frequency|undefined, frequency2:Frequency|undefined) => {
    if (!frequency1 && !frequency2) return true
    if (!frequency1 || !frequency2) return false

    return (frequency1.type === frequency2.type && (frequency1.ocurrences||0) === (frequency2.ocurrences||0) &&
        (frequency1.daysPerYear||0) === (frequency2.daysPerYear||0) &&
        (frequency1.weeksPerYear||0) === (frequency2.weeksPerYear||0) &&
        stringArraysEqual(frequency1.days, frequency2.days)
    )
}

export const createFrequency = (superType:NODE_SUPERTYPE, type?:NODE_TYPE):Frequency|undefined => {
    switch (superType) {
        case NODE_SUPERTYPE.ACTION:
            return (type && type===NODE_TYPE.CAPTURE)?{type:FREQUENCY_TYPE.WORK_DAYS, ocurrences:0}:{type:FREQUENCY_TYPE.USE_CALCULATED}
        case NODE_SUPERTYPE.ROLE:
            return {type:FREQUENCY_TYPE.USE_CALCULATED}
        case NODE_SUPERTYPE.PEOPLE:
            return {type:FREQUENCY_TYPE.WORK_DAYS, ocurrences:1}
        default:return undefined
    }
}

export const usesCalculatedFrequency = (nodeProperties:NodeProperties):boolean => {
    return (!nodeProperties?.frequency || !nodeProperties?.frequency.type || nodeProperties.frequency.type === FREQUENCY_TYPE.USE_CALCULATED)
}

export const capacityDescription = (properties?:NodeProperties) => {
    return properties && properties.frequency?`${getDurationDescription(properties.duration)} each ${properties.frequency.type.toString().toLowerCase()}`:""
}

export const actionTempoDescription = (properties?:NodeProperties) => {
    if (properties?.frequency?.type === FREQUENCY_TYPE.USE_CALCULATED)
        return `Uses input frequency and duration of ${getDurationDescription(properties.duration)}`
    else return properties?.frequency?`${properties.frequency.ocurrences} each ${properties.frequency.type.toString().toLowerCase()} with each taking ${getDurationDescription(properties.duration)}`:""
}

export const getTimesPerYearFromFrequency=(frequency:Frequency):number => {
    // TODO do we need to account for holidays...
    if (!frequency?.ocurrences) return 0

    const daysPerYear = frequency.daysPerYear||365
    const weeksPerYear = frequency.weeksPerYear||52

    switch (frequency.type) {
        case FREQUENCY_TYPE.DAY:
            return daysPerYear*frequency.ocurrences
        case FREQUENCY_TYPE.FORTNIGHT:
            return (weeksPerYear/2)*frequency.ocurrences
        case FREQUENCY_TYPE.MONTH:
            return 12*frequency.ocurrences
        case FREQUENCY_TYPE.QUARTER:
            return 4*frequency.ocurrences
        case FREQUENCY_TYPE.SPECIFIC_DAYS:
            return frequency.days?weeksPerYear*frequency.days.length*frequency.ocurrences:0
        case FREQUENCY_TYPE.WEEK:
            return weeksPerYear*frequency.ocurrences
        case FREQUENCY_TYPE.WORK_DAYS:
            return weeksPerYear*5*frequency.ocurrences
        case FREQUENCY_TYPE.YEAR:
            return frequency.ocurrences
        default: return 0
    }
}

const RED = "color:red;"
const ORANGE = "color:orange;"
const GREEN = "color:green;"

// defaultHours: the organisation's full-time year, which a role with no capacity of its own is given for each of its people
export const getTempoMessageAndColour = (superType:NODE_SUPERTYPE, nodeProperties:NodeProperties, insights:NodeInsights, projectKey:string, linqsetNode?:LinqsetNode, defaultHours = 0):{colour:string, message:string} => {
    const tempo = {colour:GREEN, message:""}

    if (superType === NODE_SUPERTYPE.ACTION) {
        if (linqsetNode) {
            if (!insights.numberProcessed && insights.isInput && (!nodeProperties.frequency || !nodeProperties.frequency.ocurrences || nodeProperties.frequency.type === FREQUENCY_TYPE.USE_CALCULATED)) {
                tempo.message = "Missing frequency, click here to add one."
                tempo.colour = RED
            }else if (getDurationHours(nodeProperties.duration) === 0 && linqsetNode.type !== NODE_TYPE.CAPTURE && linqsetNode.type !== NODE_TYPE.OUTCOME) {
                // where items wait, the duration is how long they wait
                tempo.message = nodeProperties.workType === WORK_TYPE.WAITING ? "How long do items wait here? Click here to add it." : "Missing duration, click here to add one."
                tempo.colour = RED
            }else if (insights && !insights.numberProcessed) {
                tempo.message = "Number processed is zero. This could be because a previous input is zero or no frequency set on capture. Click to edit."
                tempo.colour = RED
            }else if (usesCalculatedFrequency(nodeProperties)) {
                tempo.message = insights?.workKind === WORK_TYPE.WAITING
                    ? `Uses input frequency of ${insights.numberProcessed} per annum, with each waiting ${getDurationDescription(nodeProperties.duration).trim()} here. Click to edit.`
                    : `Uses input frequency of ${insights.numberProcessed} per annum, with each taking ${getDurationDescription(nodeProperties.duration)}. Click to edit.`
            }else {
                if (!nodeProperties.frequency?.ocurrences) tempo.colour = RED
                else tempo.colour = linqsetNode.type === NODE_TYPE.CAPTURE?GREEN:ORANGE

                tempo.message = `${actionTempoDescription(nodeProperties)}. Click to edit.`
            }
        }else {
            if (nodeProperties.frequency && nodeProperties.duration)
                tempo.message = `Multiple nodes selected. ${actionTempoDescription(nodeProperties)}`
            else tempo.message = "Multiple nodes selected. Click here to set frequency and duration for all."
        }
    }else if (isRolePeople(superType)) {
        if (linqsetNode) {
            const organisationInsights = getInsightsForNode(linqsetNode, ORGANISATION_MODEL_KEY, projectKey)

            if (superType === NODE_SUPERTYPE.PEOPLE || !insights?.hasPeople) {
                // capacity set on this node
                if (superType === NODE_SUPERTYPE.ROLE && usesCalculatedFrequency(nodeProperties)) {
                    tempo.message = defaultHours > 0
                        ? `Using the default capacity from the organisation settings: ${defaultHours.toLocaleString()} hours a year for each person. Add people or click here to set a capacity.`
                        : "Using the default capacity from the organisation settings. None is set there, so capacity matches the hours needed and there are no capacity insights. Add people or click here to set a capacity."
                    tempo.colour = ORANGE
                }else if (getDurationHours(nodeProperties.duration) > 0 && nodeProperties.frequency?.ocurrences) {
                    tempo.message = `${capacityDescription(linqsetNode.properties)}. Click to edit.`
                }else {
                    tempo.message = "Invalid capacity. Click here to set capacity."
                    tempo.colour = RED
                }
            }else if (organisationInsights?.modelledCapacity) {
                tempo.message = `Modelled capacity is ${organisationInsights?.modelledCapacity||0} hours per annum. Edit attached people to change capacity.`
            }else {
                tempo.message = "No capacity set on people, Edit attached people capacity."
                tempo.colour = RED
            }
        }else {
            if (nodeProperties.frequency && nodeProperties.duration) {
                tempo.message = `Capacity of ${capacityDescription(nodeProperties)}`
            }else if (superType === NODE_SUPERTYPE.PEOPLE || !insights?.hasPeople) {
                tempo.message = "Multiple nodes selected. Click here to set capacity for roles without people attached."
            }else {
                tempo.message = "Multiple nodes selected. All have people attached, so edit the people to change capacity."
            }
        }
    }
    return tempo
}

export const convertModelledCapacityToTotal = (modelledCapacity:number, percentageModelled:number=100) => {
    if (!percentageModelled || percentageModelled === null || percentageModelled > 100) percentageModelled = 100
    return modelledCapacity * 100/(percentageModelled||100)
}

export const getCarbonEmissions = (node:LinqsetNode, insights:NodeInsights, organisation:Organisation, numberPeople:number):Allocations[] => {
    if (!node.properties?.emissions) return []

    const modelledCapacity = (insights.modelledCapacity||insights.hours)
    const totalCapacity = convertModelledCapacityToTotal(modelledCapacity, node.properties?.percentageModelled)

    const allocatedHours = insights.hours
    const unallocatedHours = modelledCapacity?modelledCapacity-allocatedHours:0
    const unmodelledHours = totalCapacity-modelledCapacity

    const scopeMap = node.properties.emissions.reduce((m,e) => {
        const ef = organisation.emissionFactors?.find(ef => ef._key === e.emissionFactorKey)
        if (ef) {
            if (!m[ef.scope]) m[ef.scope] = createEmissionsInsights(ef.scope)
            m[ef.scope].allocatedHours = allocatedHours

            // an amount not filled in counts as none, rather than making the emissions NaN
            let quantity = ef.type === EMISSION_FACTOR_TYPE.NO_USAGE?1:(Number(e.value)||0)

            if (ef.type === EMISSION_FACTOR_TYPE.DISTANCE) quantity = quantity*(Number(e.value2)||0)
            if (e.rate === EMISSIONS_RATE.PEOPLE_YEAR || e.rate === EMISSIONS_RATE.PEOPLE_LIFETIME) {
                quantity = quantity*numberPeople
            }else if (e.rate === EMISSIONS_RATE.UNITS_HOUR || e.rate === EMISSIONS_RATE.UNITS_YEAR || e.rate === EMISSIONS_RATE.UNITS_LIFETIME) {
                quantity = quantity*(node.properties?.units||1)
            }
            if (e.rate === EMISSIONS_RATE.LIFETIME || e.rate === EMISSIONS_RATE.PEOPLE_LIFETIME || e.rate === EMISSIONS_RATE.UNITS_LIFETIME) {
                quantity = quantity/(node.properties?.lifetime||1)
            }

            const emissions = (Number(ef.value)||0) * quantity

            if (totalCapacity || e.rate === EMISSIONS_RATE.HOUR || e.rate === EMISSIONS_RATE.UNITS_HOUR) {
                const emissionsPerHour = (e.rate === EMISSIONS_RATE.HOUR || e.rate === EMISSIONS_RATE.UNITS_HOUR)?emissions:emissions/totalCapacity

                m[ef.scope].allocated += emissionsPerHour * allocatedHours
                m[ef.scope].unallocated += emissionsPerHour * unallocatedHours
                m[ef.scope].unmodelled += emissionsPerHour * unmodelledHours
            }else {
                // no capacity set and not hourly rate, so just set allocated
                m[ef.scope].allocated += emissions
            }
            m[ef.scope].allocatedHours = allocatedHours
            m[ef.scope].unallocatedHours = unallocatedHours
            m[ef.scope].unmodelledHours = unmodelledHours
        }
        return m
    }, {} as any)

    return Object.values(scopeMap)
}

export const getCost = (node:LinqsetNode, insights:NodeInsights, hours:number, numberPeople:number=1):Allocations => {
    const modelledCapacity = (insights.modelledCapacity||insights.hours)
    const totalCapacity = convertModelledCapacityToTotal(modelledCapacity, node.properties?.percentageModelled)

    let annualCost = 0
    if (isSystemInfrastructure(node.superType)) annualCost = Number(node.properties?.fixedCost||0)
    if (node.superType === NODE_SUPERTYPE.INFRASTRUCTURE) annualCost = annualCost + ((node.properties?.units||1)*(Number(node.properties?.unitCost||0)))

    annualCost = (annualCost + (numberPeople*(Number(node.properties?.annualCost||0))))

    const hourlyRate = (totalCapacity?(annualCost/totalCapacity):0) + (Number(node.properties?.hourlyCost||0))

    if (hours === 0 && totalCapacity === 0) {
        // nothing modelled uses it: the modelled part of its cost is unallocated. Modelled defaults to all of it, as elsewhere
        const unallocated = (node.properties?.percentageModelled||100)*annualCost/100
        return {allocated:0, unallocated, unmodelled:annualCost-unallocated} as Allocations
    }

    const unallocatedHours = modelledCapacity-hours
    const unmodelledHours = totalCapacity-modelledCapacity

    const allocation = {allocated:hourlyRate*hours, unallocated:hourlyRate*unallocatedHours, unmodelled:hourlyRate*unmodelledHours} as Allocations
    if (allocation.unallocated < 0) {
        // we have gone over capacity so set it to zero
        allocation.allocated += allocation.unallocated
        allocation.unallocated = 0
    }
    return allocation
}

const createEmissionsInsights = (scope:CARBON_SCOPE) => {
    return {...createAllocations(), scope} as Allocations
}

export const createCostAllocation = (superType:NODE_SUPERTYPE) => {
    return {...createAllocations(), superType} as Allocations
}

export const addAllocations = (allocationsFrom:Allocations, allocationsTo:Allocations) => {
    allocationsTo.unallocated += allocationsFrom.unallocated
    allocationsTo.allocated += allocationsFrom.allocated
    allocationsTo.unmodelled += allocationsFrom.unmodelled
}

export const addEmissions = (emissionsFrom:Allocations[], emissionsTo:Allocations[]) => {
    emissionsFrom.forEach(from => {
        if (from.scope) {
            let to = emissionsTo.find(t => t.scope === from.scope)
            if (!to) {
                to = createEmissionsInsights(from.scope)
                emissionsTo.push(to)
            }
            addAllocations(from, to)
        }
    })
}

export const allocateEmissions = (from:Allocations[], allocatedCarbonByScope:IndexedValues, proportion:number) => {
    from.forEach(eFrom => {
        if (eFrom.scope) {
            if (!allocatedCarbonByScope.map[eFrom.scope]) allocatedCarbonByScope.map[eFrom.scope] = 0
            allocatedCarbonByScope.map[eFrom.scope] += eFrom.allocated*proportion
        }
    })
}

export const getTotalAllocated = (allocations:Allocations[]) => {
    return allocations.reduce((t:number, e:Allocations) => {
        t += e.allocated
        return t
    }, 0) as number
}

export const initialiseIndexedValues = (indexes:any[]):IndexedValues => {
    const v = {map:{}, total:0} as IndexedValues
    indexes.forEach(i => {
        v.map[i] = 0
    })
    return v
}

export const getIndexValue = (values:IndexedValues|undefined, key:any) => {
    return (values && values.map[key])||0
}

// sets one value, keeping the total right. Setting zero replaces what was there (it used to be ignored, leaving the
// old value); anything that isn't a number is ignored
export const setIndexValue = (values:IndexedValues, key:any, value:number) => {
    if (!values || typeof value !== 'number' || isNaN(value)) return
    values.total -= values.map[key]||0
    values.map[key] = value
    values.total += value
}

export const addToIndexValue = (values:IndexedValues|undefined, key:any, value:number) => {
    if (!values || !value) return
    if (!values.map[key]) {
        values.map[key] = 0
    }
    values.map[key] += value
    values.total += value
}

export const multiplyIndexedValues = (values:IndexedValues, n:number) => {
    Object.keys(values.map).forEach(s => {
        values.map[s] = values.map[s] * n
    })
    values.total = values.total * n
}

export const stringArraysEqual = (arr1:string[]|undefined, arr2:string[]|undefined) => {
    if (!arr1 && !arr2) return true
    if (!arr1 || !arr2) return false
    if (arr1.length !== arr2.length) return false

    for (let i:number=0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) return false
    }
    return true
}

export const nodePropertiesEquals = (props1:NodeProperties, props2:NodeProperties) => {
    if (!props1 && !props2) return true
    if (!props1 || !props2) return false

    if (durationEquals(props1.duration, props2.duration) && frequencyEquals(props1.frequency, props2.frequency) &&
        props1.actionProcessType === props2.actionProcessType &&
        (props1.outputValue||0) === (props2.outputValue||0) &&
        (props1.outputRevenue||0) === (props2.outputRevenue||0) &&
        (props1.fixedCost||0) === (props2.fixedCost||0) &&
        (props1.annualCost||0) === (props2.annualCost||0) &&
        (props1.unitCost||0) === (props2.unitCost||0) &&
        (props1.hourlyCost||0) === (props2.hourlyCost||0) &&
        (props1.percentageModelled||0) === (props2.percentageModelled||0) &&
        (props1.people||0) === (props2.people||0) &&
        (props1.units||0) === (props2.units||0) &&
        (props1.lifetime||0) === (props2.lifetime||0)) {

        if (!stringArraysEqual(props1.emissions && props1.emissions.map(e => e._key), props2.emissions && props2.emissions.map(e => e._key))) return false
        if (!stringArraysEqual(props1.tagKeys && Object.keys(props1.tagKeys).map((t:string) => {return props1.tagKeys[t]}), props2.tagKeys && Object.keys(props2.tagKeys).map((t:string) => {return props2.tagKeys[t]}))) return false
        return true
    }
    return false

}

export const createNodeProperties = (superType?:NODE_SUPERTYPE, type?:NODE_TYPE) => {
    return {
        frequency: superType?createFrequency(superType, type):undefined,
        actionProcessType: ACTION_FREQUENCY_TYPE.SUM,
        tagKeys: {},
        fixedCost: 0,
        annualCost: 0,
        unitCost: 0,
        hourlyCost: 0,
        hourlyCarbon: 0,
        outputValue: 0,
        outputRevenue: 0,
        fixedPerUnitCarbon: 0,
        percentageModelled:100,
        people:0,
        units: (superType && superType===NODE_SUPERTYPE.INFRASTRUCTURE)?1:0,
        lifetime: (superType && superType===NODE_SUPERTYPE.INFRASTRUCTURE)?1:0
    } as unknown as NodeProperties
}

const stringsEqual = (a:string[], b:string[]):boolean => {
    if (a.length != b.length) return false
    for (const s of a) {
        if (b.indexOf(s) < 0) return false
    }
    for (const s of b) {
        if (a.indexOf(s) < 0) return false
    }
    return true
}

export const updateTagsOnProperties = (organisation:Organisation, properties:NodeProperties, tags:any[]) => {
    if (stringsEqual(Object.values(properties.tagKeys || {}), tags)) return

    const nodeTagKeys:any = {}

    tags.forEach(k => {
        const result = findTagForOrganisation(organisation, k)
        // if there is no existig value for this group or the existing value is different
        if (result && (!nodeTagKeys[result.group._key] || !properties.tagKeys || properties.tagKeys[result.group._key] !== k)) nodeTagKeys[result.group._key] = k
    })
    properties.tagKeys = nodeTagKeys
}

export const nodeHasCosts = (superType:NODE_SUPERTYPE) => {
    return isRolePeople(superType) || isSystemInfrastructure(superType)
}

export const isRolePeople = (superType:NODE_SUPERTYPE) => {
    return (superType === NODE_SUPERTYPE.ROLE || superType === NODE_SUPERTYPE.PEOPLE)
}

export const isSystemInfrastructure = (superType:NODE_SUPERTYPE) => {
    return (superType === NODE_SUPERTYPE.SYSTEM || superType === NODE_SUPERTYPE.INFRASTRUCTURE)
}

export const isRoleSystem = (superType:NODE_SUPERTYPE) => {
    return (superType === NODE_SUPERTYPE.ROLE || superType === NODE_SUPERTYPE.SYSTEM)
}

export const isRoleSystemNote = (superType:NODE_SUPERTYPE) => {
    return (superType === NODE_SUPERTYPE.ROLE || superType === NODE_SUPERTYPE.SYSTEM || superType === NODE_SUPERTYPE.NOTES)
}

export const isEditableOrganisationNode = (superType:NODE_SUPERTYPE) => {
    return (superType === NODE_SUPERTYPE.PEOPLE || superType === NODE_SUPERTYPE.INFRASTRUCTURE || superType === NODE_SUPERTYPE.NOTES)
}

export const isSupplyChain = (superType:NODE_SUPERTYPE) => {
    return (superType === NODE_SUPERTYPE.ACTION || superType === NODE_SUPERTYPE.INFORMATION || superType === NODE_SUPERTYPE.OUTCOMES)
}

export const getInsightsForNode = (node:LinqsetNode, modelKey:string, workspaceKey:string) => {
    if (!node.insights) return undefined
    return node.insights.find(i => i.modelKey === modelKey && i.projectKey === workspaceKey)
}

// in the organisation view, actions and information keep the insights of the model they belong to
const insightsModelKey = (node:LinqsetNode, linqset:Linqset) => linqset.isOrganisationModel()&&isModelNode(node.superType)?node.modelKey:linqset.modelKey

export const getInsightsWithoutCreate = (node:LinqsetNode, linqset:Linqset) => {
    return getInsightsForNode(node, insightsModelKey(node, linqset), linqset.workspaceKey)
}

export const getInsights = (node:LinqsetNode, linqset:Linqset) => {
    let insights = getInsightsWithoutCreate(node, linqset)
    if (!insights) {
        if (!node.insights) node.insights = []
        // made for the model they are looked up by: otherwise they aren't found again, and another is added each time
        insights = createNewNodeInsights(linqset.organisation._key, linqset.workspaceKey, insightsModelKey(node, linqset), node._key, isOrganisationNode(node.superType))
        node.insights.push(insights)
    }
    return insights
}

export const createLinqsetNodeWithoutLinqset = (_key:string, superType:NODE_SUPERTYPE, type:NODE_TYPE, organisationKey:string, modelKey:string, workspaceKey:string, name?:string):LinqsetNode => {
    const n = {
        _key,
        organisationKey,
        superType,
        type,
        name:name||"",
        external:false,
        modelKey:isModelNode(superType)?modelKey:ORGANISATION_MODEL_KEY,
        projectKey: workspaceKey,
        properties: createNodeProperties(superType, type),
        insights: [],
    } as unknown as LinqsetNode

    return n
}

export const createLinqsetNode = (_key:string, superType:NODE_SUPERTYPE, type:NODE_TYPE, linqset:Linqset, name?:string):LinqsetNode => {
    return createLinqsetNodeWithoutLinqset(_key, superType, type, linqset.organisationKey, linqset.modelKey, linqset.workspaceKey, name)
}

// a copy with its own properties: tags and emissions are copied too, so changing the copy's leaves the original's alone
export const cloneLinqsetNode = (node:LinqsetNode):LinqsetNode => {
    const properties = node.properties?structuredClone(node.properties):createNodeProperties(node.superType, node.type)
    return {...node, _key: v4(), _id:undefined, _rev:undefined, properties, insights: []} as unknown as LinqsetNode
}

export const resetNodeForModel = (node:LinqsetNode, linqset:Linqset):NodeInsights => {
    const insights = getInsights(node, linqset)

    resetNodeCostsForModel(insights)

    // we do this so that keeps the same object and hence will update screen when results change
    insights.actionType = ACTION_TYPE.NONE
    insights.hasPeople = false
    insights.numPeople = 0
    insights.hours=0
    insights.waitHours=0
    insights.numberProcessed=0
    insights.numActionsSupported=0
    insights.calculatedNumberProcessed=0
    insights.numInfoSupplied=0
    insights.timeUsedByRole=createIndexedValues()

    insights.isOutput = false
    insights.isLastNode = false
    insights.isInput = false

    // how the information is used, and the value derived from it, are read from the graph again (information-value.ts)
    insights.consumers = 0
    insights.consumerModels = 0
    insights.outcomesReached = 0
    insights.externalConsumers = 0
    insights.derivedValue = 0
    insights.onDetour = false
    insights.detourSplitKey = undefined
    insights.detourJoinKey = undefined
    insights.detourShare = 0
    insights.exceptionWork = false
    return insights
}

export const resetNodeCostsForModel = (insights:NodeInsights) => {
    // we do this so that keeps the same object and hence will update screen when results change
    insights.modelledCapacity=0
    insights.costAllocation = createAllocations()
    insights.allocatedCostsByType = createIndexedValues()
    insights.allocatedCostsByNode = createIndexedValues()
    insights.proportionOfPersonInRole = createIndexedValues()
    insights.inputProportions = createIndexedValues()
    insights.previousNodeProportions = createIndexedValues()

    insights.allocatedCarbonByScope = createIndexedValues()
    insights.emissions = []
    insights.totalHours=0
    insights.totalAllocatedCostsByType = createIndexedValues()
    insights.totalCarbon=0
    insights.value=0
    insights.revenue=0
    insights.outputsSupported=0
}

export const createNewNodeInsights = (organisationKey:string, projectKey:string, modelKey:string, nodeKey:string, isOrganisationNode:boolean):NodeInsights => {
    return {
        _key:v4(),
        nodeKey,
        organisationKey,
        modelKey,
        projectKey,
        isOrganisationNode,
        hasRole:false,
        hasSystem:false,
        numPeople:0,
        costAllocation:createAllocations(),
        allocatedCostsByType:createIndexedValues(),
        allocatedCarbonByScope:createIndexedValues(),
        totalAllocatedCostsByType:createIndexedValues(),
        timeUsedByRole:createIndexedValues(),
        inputProportions:createIndexedValues(),
        previousNodeProportions:createIndexedValues(),
        hours:0,
        proportion:0,
        numOutgoingEdges:0,
        modelledCapacity:0,
        numberProcessed:0,
        numActionsSupported:0,
        emissions:[],
        value:0,
        revenue:0,
        outputsSupported:0,
        consumers:0,
        consumerModels:0,
        outcomesReached:0,
        externalConsumers:0,
        derivedValue:0,
        onDetour:false,
        detourShare:0,
        exceptionWork:false,
        waitHours:0,
        totalHours:0,
        totalCarbon:0,
        numInfoSupplied:0,
        isOutput:false,
        isLastNode:false,
        isInput:false,
        actionType: ACTION_TYPE.NONE
    } as unknown as NodeInsights
}

export const addNodeInsights = (from:NodeInsights, to:NodeInsights) => {
    to.actionType = ACTION_TYPE.NONE
    addAllocations(from.costAllocation, to.costAllocation)
    addIndexedValues(from.allocatedCostsByType, to.allocatedCostsByType)
    addIndexedValues(from.allocatedCarbonByScope, to.allocatedCarbonByScope)
    addIndexedValues(from.totalAllocatedCostsByType, to.totalAllocatedCostsByType)
    addIndexedValues(from.timeUsedByRole, to.timeUsedByRole)

    from.emissions.forEach(e => {
        const existing = to.emissions.find(e2 => e2.scope === e.scope)
        if (existing) {
            addAllocations(e, existing)
        }else to.emissions.push({...e})
    })

    to.hours += from.hours
    to.proportion += from.proportion
    to.numOutgoingEdges += from.numOutgoingEdges
    to.modelledCapacity += from.modelledCapacity
    to.numberProcessed += from.numberProcessed
    to.numActionsSupported += from.numActionsSupported
    to.value += from.value
    to.outputsSupported += from.outputsSupported
    to.consumers = (to.consumers||0) + (from.consumers||0)
    to.consumerModels = (to.consumerModels||0) + (from.consumerModels||0)
    to.outcomesReached = (to.outcomesReached||0) + (from.outcomesReached||0)
    to.externalConsumers = (to.externalConsumers||0) + (from.externalConsumers||0)
    // a derived value is a ranking, not a quantity, so a total takes the highest
    to.derivedValue = Math.max(to.derivedValue||0, from.derivedValue||0)
    to.numInfoSupplied += from.numInfoSupplied

    to.totalCarbon += from.totalCarbon
    to.totalHours += from.totalHours
    to.revenue += from.revenue
    // contributions add up, so a group's is the sum of its nodes'
    to.contributedValue = (to.contributedValue||0) + (from.contributedValue||0)
    to.contributedRevenue = (to.contributedRevenue||0) + (from.contributedRevenue||0)
}

export const roundNodeInsights = (insights:NodeInsights) => {
    if (!insights) return
    insights.hours = Math.round(insights.hours)
    if (insights.waitHours) insights.waitHours = Math.round(insights.waitHours)
    insights.modelledCapacity = Math.round(insights.modelledCapacity)

    insights.numberProcessed = Math.round(insights.numberProcessed)
    insights.calculatedNumberProcessed = Math.round(insights.calculatedNumberProcessed)

    if (!insights.costAllocation) insights.costAllocation = createAllocations()
    if (!insights.emissions) insights.emissions = []
    roundAllocations([insights.costAllocation])
    roundAllocations(insights.emissions)

    roundIndexedValues(insights.allocatedCostsByType)
    roundIndexedValues(insights.allocatedCostsByNode)

    roundIndexedValues(insights.totalAllocatedCostsByType)
    roundIndexedValues(insights.allocatedCarbonByScope)
    if (insights.timeUsedByRole) roundIndexedValues(insights.timeUsedByRole)

    insights.revenue = Math.round(insights.revenue)
    insights.value = Math.round(insights.value)
    insights.derivedValue = Math.round(insights.derivedValue||0)
    insights.totalHours = Math.round(insights.totalHours)
    insights.totalCarbon = Math.round(insights.totalCarbon)
}

const addHoursToTotal = (total:NodeInsights, insights:NodeInsights) => {
    if (!total || !insights) return

    total.hours += insights.hours||0
    total.numberProcessed += insights.numberProcessed||0
    total.numInfoSupplied += insights.numInfoSupplied||0
    total.numActionsSupported += insights.numActionsSupported||0
    total.numOutgoingEdges += insights.numOutgoingEdges||0

    if (insights.timeUsedByRole && insights.timeUsedByRole.map) {
        if (!total.timeUsedByRole || !total.timeUsedByRole.map) total.timeUsedByRole = createIndexedValues()
        addIndexedValues(insights.timeUsedByRole, total.timeUsedByRole)
    }
}

export const resetCostsAndTotalHours = (node:LinqsetNode, orgLinqset:Linqset):NodeInsights => {
    const organisationInsights = resetNodeForModel(node, orgLinqset)

    node.insights.filter(i => i.projectKey === orgLinqset.workspaceKey && i.modelKey !== ORGANISATION_MODEL_KEY).forEach(i => {
        resetNodeCostsForModel(i)
        addHoursToTotal(organisationInsights, i)
    })

    node.insights.filter(i => i.projectKey === orgLinqset.workspaceKey && i.modelKey !== ORGANISATION_MODEL_KEY).forEach(i => {
        i.proportion = organisationInsights.hours?i.hours/organisationInsights.hours:0
    })

    const typeTotal = orgLinqset.insights?.superTypes[node.superType]
    if (typeTotal)
        organisationInsights.proportion = typeTotal.hours?organisationInsights.hours/typeTotal.hours:(typeTotal.number?1/typeTotal.number:0)

    return organisationInsights
}

export class NodeWarning {
    constructor(public node:LinqsetNode, public warnings: NODE_WARNING[]) {
    }
}

export const getWarningsForLinqset = (linqset:Linqset, preferences?:INSIGHTS_FIELD[]) => {
    return linqset.nodes.reduce((a:NodeWarning[], n:LinqsetNode) => {
        const warnings = getWarningsForNode(n, linqset, preferences)
        if (warnings.length > 0) {
            a.push(new NodeWarning(n, warnings))
        }
        return a
    }, [])
}

const includeWarning = (field:INSIGHTS_FIELD, preferences?:INSIGHTS_FIELD[]) => {
    if (!preferences) return true
    return preferences.indexOf(field) >= 0
}

// the information nodes joined into an action of another model. What their items are worth is realised there, so they
// carry no value or revenue of their own: that comes back from the models they feed
export const joinedInformationKeys = (edges:LinqsetEdge[]) => new Set(edges.filter(e => e.type === EDGE_TYPE.JOINS).map(e => e.sourceKey))

export const getWarningsForNode = (node:LinqsetNode, linqset:Linqset, preferences?:INSIGHTS_FIELD[], ignoreConnections:boolean=false) => {
    const warnings:NODE_WARNING[] = []
    const insights = getInsightsWithoutCreate(node, linqset)

    if (!node.name || node.name.length === 0) warnings.push(NODE_WARNING.MISSING_NAME)

    if (includeWarning(INSIGHTS_FIELD.VALUE, preferences)) {
        if (node.superType === NODE_SUPERTYPE.INFORMATION && node.modelKey && insights && insights.isOutput && !insights.joinedOut && !node.properties?.outputValue) warnings.push(NODE_WARNING.MISSING_OUTPUTVALUE)
        if (node.superType === NODE_SUPERTYPE.INFORMATION && insights?.joinedOut && (node.properties?.outputValue || node.properties?.outputRevenue)) warnings.push(NODE_WARNING.JOINED_VALUE)
    }

    if (includeWarning(INSIGHTS_FIELD.ANNUAL_HOURS, preferences)) {
        if (node.superType === NODE_SUPERTYPE.ACTION && node.type !== NODE_TYPE.OUTCOME && node.type !== NODE_TYPE.CAPTURE && node.properties && (!node.properties?.duration || getDurationHours(node.properties?.duration) === 0)) warnings.push(NODE_WARNING.MISSING_DURATION)
    }

    if (includeWarning(INSIGHTS_FIELD.PROCESSED, preferences)) {
        if (node.superType === NODE_SUPERTYPE.ACTION && node.modelKey && insights && insights.isInput && insights.numberProcessed === 0) {
            if (!node.properties?.frequency || !node.properties?.frequency.ocurrences) warnings.push(NODE_WARNING.MISSING_FREQUENCY)
        }
    }

    if (includeWarning(INSIGHTS_FIELD.CAPACITY, preferences)) {
        if (node.superType === NODE_SUPERTYPE.ROLE && (insights && insights.modelledCapacity < insights.hours)) warnings.push(NODE_WARNING.OVER_CAPACITY)
        if (node.superType === NODE_SUPERTYPE.ROLE && node.type !== NODE_TYPE.EXTERNAL_ORGANISATION && !insights?.hasPeople && !getHoursPerYear(node)) warnings.push(NODE_WARNING.MISSING_CAPACITY)
        if (node.superType === NODE_SUPERTYPE.PEOPLE && !getHoursPerYear(node)) warnings.push(NODE_WARNING.MISSING_CAPACITY)
        if (node.superType === NODE_SUPERTYPE.ROLE && node.properties?.frequency && node.properties?.frequency.type !== FREQUENCY_TYPE.USE_CALCULATED && !getHoursPerYear(node)) warnings.push(NODE_WARNING.MISSING_CAPACITY)
    }

    if (includeWarning(INSIGHTS_FIELD.CARBON, preferences)) {
        if (node.superType === NODE_SUPERTYPE.INFRASTRUCTURE && (!node.properties?.emissions || node.properties?.emissions.length===0)) warnings.push(NODE_WARNING.MISSING_CARBON)
    }

    if (includeWarning(INSIGHTS_FIELD.COST, preferences)) {
        if (node.superType === NODE_SUPERTYPE.PEOPLE && !node.properties?.annualCost && !node.properties?.hourlyCost) warnings.push(NODE_WARNING.MISSING_COST)
        if (node.superType === NODE_SUPERTYPE.ROLE && node.properties?.frequency && node.properties?.frequency.type !== FREQUENCY_TYPE.USE_CALCULATED && !node.properties?.annualCost && !node.properties?.hourlyCost) warnings.push(NODE_WARNING.MISSING_COST)
    }

    if (isRoleSystem(node.superType) && linqset.isOrganisationModel() && !node.insights?.find(i => i.modelKey !== ORGANISATION_MODEL_KEY)) warnings.push(NODE_WARNING.NOT_IN_MODEL)

    // a step the user says items wait at has nobody performing it. One LINQ reads as a wait is still flagged, in case a
    // role or system was left off it
    const waits = node.properties?.workType === WORK_TYPE.WAITING
    if (!ignoreConnections && !waits && ((node.superType === NODE_SUPERTYPE.ACTION && node.type !== NODE_TYPE.CAPTURE) || node.superType === NODE_SUPERTYPE.INFORMATION)) {
        if (!linqset.edges.find(e => e.targetKey === node._key && (e.type === EDGE_TYPE.AUTOMATE || e.type === EDGE_TYPE.PERFORM || e.type === EDGE_TYPE.SUPPLY)))
            warnings.push(NODE_WARNING.MISSING_ROLE_SYSTEM)
    }

    return warnings
}

export const getHoursPerYear=(node:LinqsetNode) => {
    const {properties, type, superType} = node
    const numPeople = (superType === NODE_SUPERTYPE.ROLE || type === NODE_TYPE.TEAM)?(properties?.people||1):1
    return (properties?.duration && properties?.frequency)?numPeople*(Math.round(getDurationHours(properties.duration) * getTimesPerYearFromFrequency(properties.frequency))):0
}

export const getTimesPerYear=(properties:NodeProperties) => {
    return properties.frequency?getTimesPerYearFromFrequency(properties.frequency):0
}

export const getHoursPerItem=(properties:NodeProperties) => {
    return properties.duration?getDurationHours(properties.duration):0
}

const addPeople = (node:LinqsetNode, organisationLinqset:Linqset, keys:string[]):number => {
    let total = 0

    if (keys.indexOf(node._key) >= 0) return total
    keys.push(node._key)

    if (node.type === NODE_TYPE.TEAM) total = node.properties?.people||1
    else if (node.superType == NODE_SUPERTYPE.PEOPLE) total = 1
    else if (node.superType === NODE_SUPERTYPE.ROLE) {
        let hasPeople = false
        organisationLinqset.edges.filter(e => e.targetKey === node._key).forEach(e => {
            const connectedNode = organisationLinqset.getNode(e.sourceKey)
            if (connectedNode && connectedNode.superType === NODE_SUPERTYPE.PEOPLE) {
                hasPeople = true
                total += addPeople(connectedNode, organisationLinqset, keys)
            }
        })
        if (!hasPeople) {
            total = node.properties?.people||1
        }
    }else if (node.superType === NODE_SUPERTYPE.SYSTEM || node.superType === NODE_SUPERTYPE.INFRASTRUCTURE) {
        organisationLinqset.edges.filter(e => e.sourceKey === node._key).forEach(e => {
            const connectedNode = organisationLinqset.getNode(e.targetKey)
            if (connectedNode && (connectedNode.superType === NODE_SUPERTYPE.ROLE || connectedNode.superType === NODE_SUPERTYPE.SYSTEM)) {
                total += addPeople(connectedNode, organisationLinqset, keys)
            }
        })
    }
    return total
}

export const getNumberPeople = (node:LinqsetNode, organisationLinqset:Linqset) => {
    const peopleKeys:string[] = []

    return addPeople(node, organisationLinqset, peopleKeys)
}

// the people of several nodes together, each person counted once however many of the nodes they are in
export const countPeople = (nodes:LinqsetNode[], organisationLinqset:Linqset) => {
    const peopleKeys:string[] = []
    return nodes.reduce((total, n) => total + addPeople(n, organisationLinqset, peopleKeys), 0)
}

export const summariseEdge = (edge:LinqsetEdge) => {
    return {
        _key:edge._key,
        type:edge.type,
        sourceKey: edge.sourceKey,
        targetKey: edge.targetKey,
        outputPercentage:edge.outputPercentage
    }
}

export const isOrganisationEdge = (edgeType:EDGE_TYPE) => {
    return (edgeType === EDGE_TYPE.FULFILS || edgeType === EDGE_TYPE.PROVIDES || edgeType === EDGE_TYPE.ATTACHED)
}

export const createLinqsetEdge = (_key:string, organisationKey:string, projectKey:string, edgeData:LinqsetEdge):LinqsetEdge => {
    const {modelKey, type, sourceKey, targetKey, outputPercentage, sourceVisualisationKey, targetVisualisationKey, createdDate, updatedDate} = edgeData
    return {
        _key,
        organisationKey,
        projectKey,
        type,
        sourceKey,
        targetKey,
        outputPercentage:outputPercentage ?? 100,        // 0% is kept: none of the items go that way
        sourceVisualisationKey,
        targetVisualisationKey,
        modelKey,
        updatedDate,
        createdDate
    } as LinqsetEdge
}

export const cloneLinqsetEdge = (edge:LinqsetEdge):LinqsetEdge => {
    return {...edge, _key: v4(), _id:undefined, _rev:undefined} as unknown as LinqsetEdge
}

export const getEdgeType = (sourceNode:LinqsetNode, targetNode?:LinqsetNode) => {
    if (targetNode && targetNode.superType === NODE_SUPERTYPE.ACTION && sourceNode.superType === NODE_SUPERTYPE.INFORMATION && sourceNode.modelKey !== targetNode.modelKey && sourceNode.external) return EDGE_TYPE.JOINS
    if (targetNode && targetNode.superType === NODE_SUPERTYPE.ACTION && sourceNode.superType === NODE_SUPERTYPE.ACTION) return EDGE_TYPE.ACTION_SUMMARY

    switch(sourceNode.superType) {
        case NODE_SUPERTYPE.ACTION:
            return EDGE_TYPE.CREATE
        case NODE_SUPERTYPE.INFORMATION:
            return targetNode && targetNode.superType === NODE_SUPERTYPE.OUTCOMES?EDGE_TYPE.REALISE:EDGE_TYPE.SUPPORT
        case NODE_SUPERTYPE.ROLE:
            return EDGE_TYPE.PERFORM
        case NODE_SUPERTYPE.PEOPLE:
            return EDGE_TYPE.FULFILS
        case NODE_SUPERTYPE.INFRASTRUCTURE:
            return EDGE_TYPE.PROVIDES
        case NODE_SUPERTYPE.NOTES:
            return EDGE_TYPE.ATTACHED
        case NODE_SUPERTYPE.OUTCOMES:
            return undefined
        case NODE_SUPERTYPE.SYSTEM:
            if (targetNode && targetNode.superType === NODE_SUPERTYPE.ROLE) return EDGE_TYPE.USEBY
            else if (targetNode && targetNode.superType === NODE_SUPERTYPE.ACTION) return EDGE_TYPE.AUTOMATE
            else return EDGE_TYPE.SUPPLY
    }
}

export const canConnect = (sourceType:NODE_TYPE, targetType:NODE_TYPE):boolean => {
    if (!sourceType || !targetType) return false;

    const sourceSuperType = getSuperType(sourceType)
    const targetSuperType = getSuperType(targetType)

    if (sourceSuperType === NODE_SUPERTYPE.NOTES) {
        return true
    }

    if (targetSuperType === NODE_SUPERTYPE.NOTES) {
        return false
    }

    if (sourceType == NODE_TYPE.OUTCOME || sourceSuperType === NODE_SUPERTYPE.OUTCOMES) return false

    if (targetSuperType == NODE_SUPERTYPE.OUTCOMES) return sourceSuperType === NODE_SUPERTYPE.INFORMATION

    if (targetType === NODE_TYPE.CAPTURE) {
        return (sourceType === NODE_TYPE.EXTERNAL_ORGANISATION || sourceType === NODE_TYPE.ROLE || sourceSuperType === NODE_SUPERTYPE.SYSTEM)
    }

    // organisation cannot be a target other than from an outcome (rule above)
    if (targetType === NODE_TYPE.EXTERNAL_ORGANISATION) {
        return sourceSuperType === NODE_SUPERTYPE.SYSTEM
    }

    if (sourceSuperType === NODE_SUPERTYPE.ACTION) {
        return targetSuperType === NODE_SUPERTYPE.INFORMATION
    }

    if (sourceSuperType === NODE_SUPERTYPE.INFORMATION) {
        return targetSuperType === NODE_SUPERTYPE.ACTION
    }

    if (sourceSuperType === NODE_SUPERTYPE.PEOPLE) {
        return targetType === NODE_TYPE.ROLE
    }

    if (sourceSuperType === NODE_SUPERTYPE.ROLE) {
        return targetSuperType === NODE_SUPERTYPE.ACTION
    }

    if (sourceSuperType === NODE_SUPERTYPE.INFRASTRUCTURE) {
        return targetSuperType === NODE_SUPERTYPE.SYSTEM
    }

    if (sourceSuperType === NODE_SUPERTYPE.SYSTEM) {
        return (targetSuperType === NODE_SUPERTYPE.ROLE || targetSuperType === NODE_SUPERTYPE.INFORMATION || targetSuperType === NODE_SUPERTYPE.ACTION)
    }

    return false
}

// used to reset model insights when making a change. costs all get reset, but keep hours and model node numbers
export const resetAllModelInsights = (organisationLinqset:Linqset, models:ModelDetails[]) => {
    const {insights, workspaceKey} = organisationLinqset

    if (insights) resetModelInsightsCosts(insights)

    models.forEach(m => {
        const si = m.insights.find(i => i.projectKey === workspaceKey)

        if (si) {
            resetModelInsightsCosts(si)
            // reset time for roles and systems as they get added to when distributing costs
            si.superTypes[NODE_SUPERTYPE.ROLE].hours = 0
            si.superTypes[NODE_SUPERTYPE.SYSTEM].hours = 0
            si.externalOrgs.hours = 0
        }
    })

    return insights
}

const processNextModel = (model:ModelDetails, models:ModelDetails[], orderedModels:ModelDetails[], keys:string[], organisationLinqset:Linqset) => {
    orderedModels.push(model)
    keys.push(model._key)
    const infoNodes = organisationLinqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.INFORMATION && n.modelKey === model._key)
    infoNodes.forEach(infoNode => {
        const actionNodes = organisationLinqset.edges.filter(e => e.sourceKey === infoNode._key && e.type === EDGE_TYPE.JOINS).map(e => organisationLinqset.nodes.find(an => an._key === e.targetKey))
        actionNodes.forEach(an => {
            const model = an && models.find(nm => nm._key === an.modelKey && keys.indexOf(nm._key) < 0)
            if (model) {
                processNextModel(model, models, orderedModels, keys, organisationLinqset)
            }
        })
    })
}

export const getConnectedModels = (model:ModelDetails, models:ModelDetails[], organisationLinqset:Linqset) => {
    const keys:string[] = []
    const connectedModels:ModelDetails[] = []
    processNextModel(model, models, connectedModels, keys, organisationLinqset)
    return connectedModels
}

// the organisation's information assets for choosing from: those the model already uses first, then by name. A new
// list, so the organisation's own keeps its order
export const getOrderedInformationAssets = (organisation:Organisation, linqset?:Linqset) => {
    if (!organisation.informationAssets) return []
    const used = new Set((linqset?.insights?.informationAssets || []).map(ia => ia.groupKey))
    return [...organisation.informationAssets].sort((a, b) => {
        const aUsed = used.has(a._key), bUsed = used.has(b._key)
        if (aUsed !== bUsed) return aUsed ? -1 : 1
        return (a.name || '').localeCompare(b.name || '', undefined, {sensitivity: 'base'})
    })
}
